var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import Container from "./Panel.js";
import DateHelper from "../helper/DateHelper.js";
const hours = Array.from(Array(24)).map((_, i) => {
  return { id: i, text: String(i + 100).slice(1) };
}), seconds = Array.from(Array(60)).map((_, i) => {
  return { id: i, text: String(i + 100).slice(1) };
});
class TimePicker extends Container {
  //endregion
  alignTo() {
    const { widgetMap } = this, { minute, second } = this.owner.input;
    this.getConfig("is24Hour");
    if (this.minimizeHeight) {
      widgetMap.hour.maxHeight = widgetMap.second.maxHeight = widgetMap.minute.height;
    }
    widgetMap.minute[minute ? "show" : "hide"]();
    widgetMap.second[second ? "show" : "hide"]();
    return super.alignTo(...arguments);
  }
  updateIs24Hour(is24Hour) {
    this.widgetMap.hour.store.filter((t) => is24Hour ? true : t.id < 12);
  }
  updateStep(step) {
    step = DateHelper.as("m", step.magnitude, step.unit);
    const minuteValues = [];
    for (let minutes = 0; minutes < 60; minutes += step) {
      minuteValues.push({ id: minutes, text: String(100 + minutes).slice(1) });
    }
    this.widgetMap.minute.store.data = minuteValues;
  }
  //region Event listeners
  onSelectionChange({ source, selected: [selectedItem] }) {
    const me = this;
    me._isUserAction = source._isUserAction;
    source.navigator.activeItem = selectedItem;
    if (!me.updatingValue) {
      me.owner.input.setSubFieldValue(source.ref, selectedItem.id);
    }
    me._isUserAction = false;
  }
  //endregion
  //region Internal functions
  triggerTimeChange(time) {
    this.trigger("timeChange", { time });
  }
  //endregion
  //region Getters / Setters
  updateInitialValue(initialValue) {
    this.value = initialValue;
  }
  changeValue(value) {
    if (value) {
      value = typeof value === "string" ? DateHelper.parse(value, this.format) : value;
    }
    return value != null ? value : DateHelper.getTime(0);
  }
  updateValue(value) {
    const me = this, {
      hour,
      minute,
      second
    } = me.widgetMap, h = value.getHours(), hours2 = me.is24Hour ? h : h % 12, minutes = value.getMinutes(), seconds2 = value.getSeconds();
    me.updatingValue = true;
    hour.navigator.activeItem = new Number(hours2);
    hour.select(hours2, true);
    minute.navigator.activeItem = new Number(minutes);
    minute.select(minutes, true);
    second.navigator.activeItem = new Number(seconds2);
    second.select(seconds2, true);
    me.updatingValue = false;
    if (me.isVisible) {
      me.triggerTimeChange(value);
    }
  }
  changeMin(min) {
    return typeof min === "string" ? DateHelper.parse(min, this.format) : min;
  }
  updateMin(min) {
    const {
      hour,
      minute,
      second
    } = this.widgetMap, { owner, is24Hour } = this;
    if (min) {
      const minHours = min.getHours(), minMinutes = min.getMinutes(), minSeconds = min.getSeconds();
      hour.store.filter({
        id: "min-filter",
        filterBy: ({ id }) => {
          if (!is24Hour && owner.value.getHours() > 12) {
            id += 12;
          }
          return id >= minHours;
        }
      });
      minute.store.filter({
        id: "min-filter",
        filterBy: ({ id }) => id >= minMinutes
      });
      second.store.filter({
        id: "min-filter",
        filterBy: ({ id }) => id >= minSeconds
      });
    } else {
      hour.store.removeFilter("min-filter");
      minute.store.removeFilter("min-filter");
      second.store.removeFilter("min-filter");
    }
  }
  changeMax(max) {
    return typeof max === "string" ? DateHelper.parse(max, this.format) : max;
  }
  updateMax(max) {
    const {
      hour,
      minute,
      second
    } = this.widgetMap;
    if (max) {
      const minHours = max.getHours(), minMinutes = max.getMinutes(), minSeconds = max.getSeconds();
      hour.store.filter({
        id: "max-filter",
        filterBy: ({ id }) => id <= minHours
      });
      minute.store.filter({
        id: "max-filter",
        filterBy: ({ id }) => id <= minMinutes
      });
      second.store.filter({
        id: "max-filter",
        filterBy: ({ id }) => id <= minSeconds
      });
    } else {
      hour.store.removeFilter("max-filter");
      minute.store.removeFilter("max-filter");
      second.store.removeFilter("max-filter");
    }
  }
  //endregion
}
//region Config
__publicField(TimePicker, "$name", "TimePicker");
__publicField(TimePicker, "type", "timepicker");
__publicField(TimePicker, "configurable", {
  floating: true,
  layout: "hbox",
  defaults: {
    type: "list",
    displayField: "id",
    itemTpl(r) {
      const value = r.id, displayVal = this.ref !== "hour" || this.owner.is24Hour || value ? value : 12;
      return String(displayVal + 100).slice(1);
    },
    itemCls: "b-timepicker-item",
    itemsFocusable: false,
    scrollable: {
      overflowY: "hidden-scroll"
    },
    onSelectionChange: "up.onSelectionChange",
    navigator: {
      activateOnMouseover: true
    }
  },
  items: {
    hour: {
      items: hours
    },
    minute: {
      items: []
    },
    second: {
      hidden: true,
      items: seconds
    }
  },
  step: {
    magnitude: 5,
    unit: "m"
  },
  autoShow: false,
  trapFocus: true,
  /**
   * Time value, which can be a Date or a string. If a string is specified, it will be converted using the
   * specified {@link #config-format}
   * @prp {Date}
   * @accepts {Date|String}
   */
  value: {
    $config: {
      equal: "date"
    },
    value: null
  },
  /**
   * Time format. Used to set appropriate 12/24 hour format to display.
   * See {@link Core.helper.DateHelper#function-format-static DateHelper} for formatting options.
   * @prp {String}
   */
  format: null,
  /**
   * Max value, which can be a Date or a string. If a string is specified, it will be converted using the
   * specified {@link #config-format}
   * @prp {Date}
   * @accepts {Date|String}
   */
  max: null,
  /**
   * Min value, which can be a Date or a string. If a string is specified, it will be converted using the
   * specified {@link #config-format}
   * @prp {Date}
   * @accepts {Date|String}
   */
  min: null,
  is24Hour: {
    $config: "lazy",
    value: null
  },
  /**
   * Initial value, which can be a Date or a string. If a string is specified, it will be converted using the
   * specified {@link #config-format}. Initial value is restored on Escape click
   * @member {Date} initialValue
   * @accepts {Date|String}
   */
  initialValue: null
  // Not documented as config on purpose, API was that way
});
TimePicker.initClass();
TimePicker._$name = "TimePicker";
export {
  TimePicker as default
};
